/**
 * Guided walkthrough — single source of truth.
 *
 * The tour engine (use-guided-tour) and the overlay (GuidedTourOverlay) are
 * fully data-driven off this ordered array. To reorder, reword, add, or remove
 * a step you only edit this file. The engine never hard-codes step content or
 * sequence.
 *
 * The walkthrough runs one job from quote to payment. Each step belongs to a
 * setup-checklist item (`item`); the checklist's "Show me" button starts the
 * walkthrough at that item's first step (see `tourStartFor`).
 *
 * Contract for pages:
 *   1. Render an element with `data-tour-target="<targetAttr>"` somewhere the
 *      step should point at.
 *   2. When the action for an `advanceOn` step happens, call
 *      `emitTourEvent("<advanceOn>")` (from use-guided-tour). The engine
 *      advances automatically and records any refs you pass.
 *
 * Steps without an `advanceOn` are informational and advance via the tooltip's
 * "Next" button. Steps with `advanceOn` wait for the page to fire the event,
 * but may also opt into a manual Next via `allowManualNext`. Give every step a
 * way forward unless skipping it would leave later steps nothing to point at.
 */

import type { SetupGuideState, SetupStepId } from "@/hooks/use-setup-guide";

export interface TourRefs {
  customerIds: number[];
  productIds: number[];
  costIds: number[];
  projectIds: number[];
  templateIds: number[];
  quoteIds: number[];
  invoiceIds: number[];
  /** Newest quote/invoice on the account, for walkthroughs started partway. */
  latestQuoteId: number | null;
  latestInvoiceId: number | null;
}

export type TourPlacement = "top" | "bottom" | "left" | "right" | "auto";

export interface TourStepConfig {
  /** Stable id, used for analytics + keys. */
  id: string;
  /** The setup-checklist item this step teaches. */
  item: SetupStepId;
  /** Short label shown in the step counter / chrome. */
  label: string;
  /** Tooltip heading. */
  title: string;
  /** Tooltip body copy. */
  body: string;
  /**
   * The `data-tour-target` value to spotlight. If the element is missing the
   * overlay falls back to a centered card so the tour never gets stuck.
   */
  targetAttr: string;
  /** Preferred tooltip placement relative to the target. */
  placement?: TourPlacement;
  /**
   * Route the step lives on. Resolved against the current demo refs so steps
   * on dynamic routes (e.g. /quotes/:id) can point at the seeded/created
   * records. Return null to stay on the current page.
   */
  resolvePath: (refs: TourRefs) => string | null;
  /**
   * Event name the page emits to advance this step. When omitted the step is
   * informational and advances via the tooltip "Next" button.
   */
  advanceOn?: string;
  /**
   * Show a manual "Next" button even when `advanceOn` is set. Useful for steps
   * that highlight an optional action the user may skip.
   */
  allowManualNext?: boolean;
  /**
   * When true, an `advanceOn` event auto-advances immediately without waiting
   * for the user to click "Continue" in the confirmation card.
   */
  skipConfirmation?: boolean;
  /**
   * How many times `advanceOn` must fire before the step advances.
   * Defaults to 1.
   */
  minEventCount?: number;
  /**
   * Sidebar navigation href for this step. When set and the user is NOT on
   * this section, the overlay highlights the matching sidebar nav item and
   * asks the user to click it rather than auto-navigating.
   */
  navSidebarHref?: string;
  /**
   * Which corner to anchor the fixed tooltip to. Defaults to "right"
   * (bottom-right). Use "left" when the step target occupies the right
   * side of the screen so the tooltip doesn't obscure it.
   */
  tooltipSide?: "left" | "right";
}

/** Last id in the list, falling back to null. */
const last = (ids: number[]): number | null => (ids.length ? ids[ids.length - 1] : null);

/** The quote this walkthrough is working on: the one it made, else the newest. */
const tourQuote = (r: TourRefs): number | null => last(r.quoteIds) ?? r.latestQuoteId;
const tourInvoice = (r: TourRefs): number | null => last(r.invoiceIds) ?? r.latestInvoiceId;

const quotePath = (r: TourRefs) => {
  const id = tourQuote(r);
  return id ? `/quotes/${id}` : null;
};
const invoicePath = (r: TourRefs) => {
  const id = tourInvoice(r);
  return id ? `/invoices/${id}` : null;
};

export const GUIDED_TOUR_STEPS: TourStepConfig[] = [
  // ── Create your first quote ───────────────────────────────────────────────
  {
    id: "create-quote",
    item: "create-first-quote",
    label: "Create a quote",
    title: "Create a new quote",
    body: "We've picked the sample customer for you. Fill in the details and click Create to save the quote.",
    targetAttr: "quote-form",
    placement: "auto",
    resolvePath: () => "/quotes/new",
    advanceOn: "quote.created",
    skipConfirmation: true,
    navSidebarHref: "/quotes",
    tooltipSide: "left",
  },
  {
    id: "add-line-item",
    item: "create-first-quote",
    label: "Add line item",
    title: "Add a line item",
    body: "Click 'Add line item' and pick a product or service. Its labor and material costs come with it.",
    targetAttr: "quote-add-line-item",
    placement: "top",
    resolvePath: quotePath,
    advanceOn: "quote.line-item-added",
    allowManualNext: true,
    navSidebarHref: "/quotes",
  },
  {
    id: "internal-workshop",
    item: "create-first-quote",
    label: "Your profit",
    title: "See your profit before you send",
    body: "The Internal Workshop shows your costs, profit and margin, and updates as you change the quote. Only you see it; it never appears on what your customer gets.",
    targetAttr: "quote-internal-workshop",
    placement: "left",
    resolvePath: quotePath,
    allowManualNext: true,
    navSidebarHref: "/quotes",
    tooltipSide: "left",
  },
  {
    id: "preview-pdf",
    item: "create-first-quote",
    label: "Preview PDF",
    title: "Preview your quote",
    body: "Click the eye icon and choose Customer PDF to see the quote exactly as your customer will, with your business details and logo. Close it to carry on.",
    targetAttr: "quote-preview-btn",
    placement: "bottom",
    resolvePath: quotePath,
    advanceOn: "quote.preview-closed",
    skipConfirmation: true,
    allowManualNext: true,
    navSidebarHref: "/quotes",
  },
  // ── Send a quote ──────────────────────────────────────────────────────────
  {
    id: "mark-sent",
    item: "send-quote",
    label: "Send quote",
    title: "Email the quote",
    body: "Click the envelope icon to email the quote with the PDF attached. The sample customer uses your own email address, so it lands in your inbox and you see exactly what a customer gets.",
    targetAttr: "quote-send-btn",
    placement: "bottom",
    resolvePath: quotePath,
    advanceOn: "quote.sent",
    allowManualNext: true,
    navSidebarHref: "/quotes",
  },
  // ── Get paid ──────────────────────────────────────────────────────────────
  {
    id: "convert-invoice",
    item: "get-paid",
    label: "Convert to invoice",
    title: "Turn the quote into an invoice",
    body: "When the customer says yes, convert the quote into an invoice in one click. The line items carry over.",
    targetAttr: "quote-convert-btn",
    placement: "bottom",
    resolvePath: quotePath,
    advanceOn: "invoice.created",
    skipConfirmation: true,
    allowManualNext: true,
    navSidebarHref: "/quotes",
  },
  {
    id: "send-invoice",
    item: "get-paid",
    label: "Send invoice",
    title: "Send the invoice",
    body: "Click the envelope icon to email the invoice. Once it's sent you can record payments against it.",
    targetAttr: "invoice-send-btn",
    placement: "bottom",
    resolvePath: invoicePath,
    advanceOn: "invoice.sent",
    allowManualNext: true,
    navSidebarHref: "/invoices",
  },
  {
    id: "record-payment",
    item: "get-paid",
    label: "Record payment",
    title: "Record a payment",
    body: "Paid by cash, check or bank transfer? Record it here to keep the balance right. Your customer can get a branded receipt for every payment.",
    targetAttr: "invoice-record-payment-btn",
    placement: "bottom",
    resolvePath: invoicePath,
    advanceOn: "invoice.payment-recorded",
    allowManualNext: true,
    navSidebarHref: "/invoices",
    tooltipSide: "left",
  },
];

export const TOUR_STEP_COUNT = GUIDED_TOUR_STEPS.length;

/** The records the walkthrough can point at, from the setup-guide state. */
export function tourRefsFrom(state: SetupGuideState | null): TourRefs {
  const d = state?.demoData;
  return {
    customerIds: d?.customerIds ?? [],
    productIds: d?.productIds ?? [],
    costIds: d?.costIds ?? [],
    projectIds: d?.projectIds ?? [],
    templateIds: d?.templateIds ?? [],
    quoteIds: d?.quoteIds ?? [],
    invoiceIds: d?.invoiceIds ?? [],
    latestQuoteId: state?.latestQuoteId ?? null,
    latestInvoiceId: state?.latestInvoiceId ?? null,
  };
}

/**
 * Where "Show me" starts the walkthrough for a checklist item, or null when
 * the item has no walkthrough. Later items need a quote to work on; with none
 * on the account yet, start from creating one.
 */
export function tourStartFor(item: SetupStepId, refs: TourRefs): number | null {
  const first = GUIDED_TOUR_STEPS.findIndex((s) => s.item === item);
  if (first === -1) return null;
  if (item !== "create-first-quote" && tourQuote(refs) == null) return 0;
  return first;
}
